import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subject, ReplaySubject, Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil, catchError, switchMap } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Company } from 'app/models';
import * as _ from 'lodash';
import { ApiUrl } from 'app/constants/ApiUrls';
import { GringottsConstants } from 'app/helpers/constants';

@Injectable({
  providedIn: 'root'
})
export class CompanySearchService implements OnDestroy {
  private onDestroy$ = new Subject<void>();
  private _error: ReplaySubject<string> = new ReplaySubject<string>();
  public error$: Observable<string> = this._error.asObservable();
  public _loading: boolean;
  private _request: Subject<any> = new Subject();
  private _companies: ReplaySubject<Company[]> = new ReplaySubject<Company[]>();
  public companies$: Observable<any> = this._companies.asObservable();

  public companySearchString = '';

  constructor(
    private _http: HttpClient,
    private _translateService: TranslateService,
    public snackBar: MatSnackBar
  ) {
    this._request
      .pipe(
        switchMap(() => this.getData().pipe(
            catchError(error =>
              this._handleSearchError(error, this._translateService)
            )
          ))
      )
      .subscribe((data: any) => {
        if (data.errorMessage) {
          this._error.next(data.errorMessage);
        } else {
          this._error.next(null);
          this._companies.next(data as Company[]);
        }
      });
    this.clearCompanies();
  }

  clearCompanies() {
    this._companies.next(null);
  }

  searchCompanies(query: string): void {
    this.companySearchString = query;
    this._request.next(null);
  }

  sortCompanySearch(companies) {
    let companiesTop = [];
    let companiesBottom = [];
    companies.forEach(c => {
      if (c.name && !this.isEqual(c.sectorNumber, GringottsConstants.notAvailable)  && c.name.toLowerCase().indexOf(this.companySearchString.toLowerCase()) === 0) {
        companiesTop.push(c);
      } else {
        companiesBottom.push(c);
      }
    });

    companiesTop = _.orderBy(companiesTop, ['sectorNumber', 'name'], ['desc', 'asc']);
    companiesBottom = _.orderBy(companiesBottom, ['sectorNumber', 'name'], ['desc', 'asc']);

    return companiesTop.concat(companiesBottom);
  }

  private getData(): Observable<any> {
    if (this.companySearchString) {
      const encodedString = encodeURIComponent(this.companySearchString);
      return this._http.get(
        `${ApiUrl.companies}?name=${encodedString}`
      );
    } else {
      return of([]);
    }
  }

  private _handleSearchError(
    error: HttpErrorResponse,
    translateService: TranslateService
  ) {
    if (error.status === 404) {
      return of({ errorMessage: '404' });
    }
    let errorMsg = '';
    translateService
      .get('ERROR_TRY_LATER')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (errorMsg = value));
    if (error.error instanceof ErrorEvent) {
      console.error(`[company-search-service] ERROR: ${error.error.message}`);
    } else {
      console.error(
        `[company-search-service] STATUS: ${error.status}, ERROR: ${error.message}`
      );
    }
    return of({ errorMessage: errorMsg });
  }

  private isEqual(value1 : string, value2: string) : boolean{
    if(!value1 || !value2) return false;
    return value1.toLocaleLowerCase() === value2.toLocaleLowerCase();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }
}
